import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

export interface Load<T> {
  data: T | undefined;
  error: unknown;
  /** True on the first load and on reloads. */
  loading: boolean;
  reload: () => void;
  setData: Dispatch<SetStateAction<T | undefined>>;
}

function isAbort(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}

/**
 * Loads data with an AbortSignal and re-runs when deps change.
 * reload() refetches while keeping the current data on screen.
 */
export function useLoad<T>(fn: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[]): Load<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const depKey = JSON.stringify(deps);
  const lastKey = useRef(depKey);

  useEffect(() => {
    const ctl = new AbortController();
    if (lastKey.current !== depKey) {
      // Different query: drop old data so the screen does not show the wrong rows.
      setData(undefined);
      lastKey.current = depKey;
    }
    setLoading(true);
    fnRef
      .current(ctl.signal)
      .then((d) => {
        if (ctl.signal.aborted) return;
        setData(d);
        setError(null);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (ctl.signal.aborted || isAbort(e)) return;
        setError(e);
        setLoading(false);
      });
    return () => ctl.abort();
  }, [depKey, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload, setData };
}

/** Wraps an async action with busy and error state. */
export function useAction<A extends unknown[], R>(
  fn: (...args: A) => Promise<R>,
): { run: (...args: A) => Promise<R | undefined>; busy: boolean; error: unknown; clear: () => void } {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const run = useCallback(async (...args: A) => {
    setBusy(true);
    setError(null);
    try {
      return await fnRef.current(...args);
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  const clear = useCallback(() => setError(null), []);
  return { run, busy, error, clear };
}

/** Re-renders every `ms` milliseconds; returns the current time. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export function usePrefersReducedMotion(): boolean {
  const q = '(prefers-reduced-motion: reduce)';
  const [v, setV] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setV(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return v;
}
